import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, MapPin, Users, DollarSign, CheckCircle, XCircle, ArrowLeft, Download, Filter, Search, Star, MessageSquare, X, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import Header from '../components/Header';
import basketIcon from 'figma:asset/f4b6087bb5a2b853ba4fdd33278c203eb98d743e.png';
import { fetchUserBookings, type BookingData } from '../services/bookingsListService';
import { cancelBooking } from '../services/bookingService';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const brandStyles = {
  primary: 'bg-[#2B8B3B] text-white hover:bg-[#236B2F]',
  secondary: 'bg-[#F5F5DC] text-[#2B8B3B] hover:bg-[#f0ebe1] hover:text-[#2B8B3B] border-[#2B8B3B]',
  accent: 'bg-[#F5F5DC]',
  text: 'text-[#2B8B3B]'
};

// All mock data removed - using real data from Supabase via fetchUserBookings

interface BookingsPageProps {
  onNavigate: (page: 'dashboard' | 'bookings' | 'payment' | 'account', scrollToDistro?: boolean) => void;
  scrollToDistroFund?: boolean;
  onBookAgain?: (courseId: string) => void;
}

export default function BookingsPage({ onNavigate, scrollToDistroFund = false, onBookAgain }: BookingsPageProps) {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedBookingForReview, setSelectedBookingForReview] = useState<BookingData | null>(null);
  const [starRating, setStarRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedAspects, setSelectedAspects] = useState<string[]>([]);
  const [wouldPlayAgain, setWouldPlayAgain] = useState<boolean | null>(null);
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const distroFundRef = useRef<HTMLDivElement>(null);

  const [upcomingBookings, setUpcomingBookings] = useState<BookingData[]>([]);
  const [pastBookings, setPastBookings] = useState<BookingData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<BookingData | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    const loadBookings = async () => {
      if (!user?.id) return;
      
      setIsLoading(true);
      try {
        const result = await fetchUserBookings(user.id);
        setUpcomingBookings(result.upcoming);
        setPastBookings(result.past);
      } catch (error) {
        console.error('Error loading bookings:', error);
        toast.error('Failed to load bookings');
      } finally {
        setIsLoading(false);
      }
    };

    loadBookings();
  }, [user]);

  useEffect(() => {
    if (scrollToDistroFund && distroFundRef.current) {
      setTimeout(() => {
        distroFundRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [scrollToDistroFund]);

  // Dynamic aspects based on star rating
  const getAspectsForRating = (rating: number): { positive: string[], negative: string[] } => {
    switch(rating) {
      case 1:
        return {
          positive: [],
          negative: [
            'Course was in poor condition',
            'Overgrown fairways/rough',
            'Damaged or missing baskets',
            'Poor tee pad conditions',
            'No facilities available',
            'Overcrowded/long waits',
            'Poor signage/navigation'
          ]
        };
      case 2:
        return {
          positive: [
            'Acceptable for the price',
            'Some holes were enjoyable'
          ],
          negative: [
            'Course needs maintenance',
            'Limited facilities',
            'Some confusing hole layouts',
            'Crowded conditions',
            'Parking issues'
          ]
        };
      case 3:
        return {
          positive: [
            'Well-maintained course',
            'Good variety of holes',
            'Clean facilities',
            'Fair pricing',
            'Friendly staff',
            'Good for beginners'
          ],
          negative: [
            'Could use some updates',
            'Limited amenities',
            'Some challenging navigation'
          ]
        };
      case 4:
        return {
          positive: [
            'Excellent course condition',
            'Great hole variety',
            'Beautiful scenery',
            'Well-marked holes',
            'Clean restrooms',
            'Good parking',
            'Challenging but fair',
            'Great for all skill levels'
          ],
          negative: [
            'Can get busy on weekends',
            'Limited food options'
          ]
        };
      case 5:
        return {
          positive: [
            'Outstanding course design',
            'Perfect course conditions',
            'Stunning natural beauty',
            'Excellent facilities',
            'Top-notch maintenance',
            'Great pro shop',
            'Memorable experience',
            'Professional tournament quality',
            'Exceptional value',
            'Outstanding staff service'
          ],
          negative: []
        };
      default:
        return { positive: [], negative: [] };
    }
  };

  const getRatingLabel = (rating: number): string => {
    switch(rating) {
      case 1: return 'Poor';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Very Good';
      case 5: return 'Excellent';
      default: return '';
    }
  };

  const openReviewDialog = (booking: BookingData) => {
    setSelectedBookingForReview(booking);
    setReviewDialogOpen(true);
    // Reset form
    setStarRating(0);
    setHoveredStar(0);
    setSelectedAspects([]);
    setWouldPlayAgain(null);
    setWouldRecommend(null);
  };

  const toggleAspect = (aspect: string) => {
    setSelectedAspects(prev => 
      prev.includes(aspect) 
        ? prev.filter(a => a !== aspect)
        : [...prev, aspect]
    );
  };

  const handleSubmitReview = async () => {
    if (starRating === 0) {
      toast.error('Please select a star rating before submitting');
      return;
    }
    if (!selectedBookingForReview || !user?.id) return;

    setIsSubmitting(true);
    try {
      const aspects = getAspectsForRating(starRating);
      const positiveAspects = selectedAspects.filter(a => aspects.positive.includes(a));
      const negativeAspects = selectedAspects.filter(a => aspects.negative.includes(a));

      const { error } = await supabase.from('reviews').insert({
        course_id: selectedBookingForReview.courseId,
        player_id: user.id,
        booking_id: selectedBookingForReview.id,
        rating: starRating,
        positive_aspects: positiveAspects,
        negative_aspects: negativeAspects,
        would_play_again: wouldPlayAgain,
        would_recommend: wouldRecommend,
      });

      if (error) throw error;

      toast.success('Review submitted successfully! Thank you for your feedback.');
      setReviewDialogOpen(false);
      // Update local state so "Reviewed" badge shows immediately
      setPastBookings(prev =>
        prev.map(b => b.id === selectedBookingForReview.id ? { ...b, hasReview: true } : b)
      );
    } catch (err: any) {
      console.error('Failed to submit review:', err);
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isRefundEligible = (booking: BookingData): boolean => {
    const now = new Date();
    const teeTime = new Date(booking.slotStart);
    if (now >= teeTime) return false; // Past tee time — no refund
    if (booking.refundHoursSnapshot === null) return false; // No policy snapshot — can't determine
    if (booking.refundHoursSnapshot === 0) return true; // Flexible — always eligible before tee time
    const hoursUntil = (teeTime.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntil >= booking.refundHoursSnapshot;
  };

  const openCancelDialog = (booking: BookingData) => {
    setBookingToCancel(booking);
    setCancelDialogOpen(true);
  };

  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;
    setCancelLoading(true);
    const success = await cancelBooking(bookingToCancel.id);
    setCancelLoading(false);
    setCancelDialogOpen(false);
    setBookingToCancel(null);
    if (success) {
      toast.success('Booking cancelled successfully');
      if (user?.id) {
        const result = await fetchUserBookings(user.id);
        setUpcomingBookings(result.upcoming);
        setPastBookings(result.past);
      }
    } else {
      toast.error('Failed to cancel booking. Please try again.');
    }
  };

  const handleDownloadReceipt = (booking: BookingData) => {
    const lines = [
      'discgo — Booking Receipt',
      '========================',
      '',
      `Confirmation #: ${booking.confirmationNumber}`,
      `Course:         ${booking.courseName}`,
      `Location:       ${booking.location}`,
      `Date:           ${booking.date}`,
      `Time:           ${booking.time}`,
      `Players:        ${booking.players}`,
      `Total Paid:     $${booking.totalPrice}`,
      `Status:         ${booking.status}`,
      '',
      'Thank you for playing on discgo!',
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `discgo-receipt-${booking.confirmationNumber}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const BookingCard = ({ booking, isPast = false }: { booking: BookingData, isPast?: boolean }) => (
    <Card className="border-2 border-gray-100 overflow-hidden hover:border-[#2B8B3B] transition-all">
      <div className="flex flex-col sm:flex-row">
        {/* Course Image */}
        <div className="h-48 sm:w-32 sm:h-auto bg-gray-200 flex-shrink-0 flex">
          <ImageWithFallback
            src={booking.courseImage}
            alt={booking.courseName}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Booking Details */}
        <div className="flex-1 p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="mb-1">{booking.courseName}</h4>
                  <p className="text-sm text-gray-600">{booking.location}</p>
                </div>
                {booking.status === 'requested' && (
                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-500 ml-2">
                    <Clock className="w-3 h-3 mr-1" />Pending Request
                  </Badge>
                )}
                {(booking.status === 'confirmed' || booking.status === 'paid' || booking.status === 'reserved') && (
                  <Badge variant="outline" className="bg-green-50 text-green-600 border-green-500 ml-2">
                    <CheckCircle className="w-3 h-3 mr-1" />Confirmed
                  </Badge>
                )}
                {booking.status === 'completed' && (
                  <Badge variant="outline" className="bg-green-50 text-green-600 border-green-500 ml-2">
                    <CheckCircle className="w-3 h-3 mr-1" />Completed
                  </Badge>
                )}
                {booking.status === 'cancelled' && (
                  <Badge variant="outline" className="bg-red-50 text-red-600 border-red-500 ml-2">
                    <X className="w-3 h-3 mr-1" />Cancelled
                  </Badge>
                )}
                {booking.status === 'refunded' && (
                  <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300 ml-2">
                    <RotateCcw className="w-3 h-3 mr-1" />Refunded
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm text-gray-600 mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(booking.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{booking.time}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span>{booking.players} players</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  <span className="font-medium text-gray-900">${booking.totalPrice}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm text-gray-500">Confirmation #:</span>
                <span className="text-sm font-medium">{booking.confirmationNumber}</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {!isPast && (
                  <Button variant="outline" size="sm" className="rounded-lg">
                    View Details
                  </Button>
                )}
                {!isPast && (booking.status === 'confirmed' || booking.status === 'requested') && (
                  <>
                    <Button variant="outline" size="sm" className="rounded-lg">
                      <Calendar className="w-4 h-4 mr-1" />
                      Add to Calendar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                      onClick={() => openCancelDialog(booking)}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </>
                )}
                {isPast && booking.status === 'completed' && (
                  <>
                    <Button variant="outline" size="sm" className="rounded-lg" onClick={() => handleDownloadReceipt(booking)}>
                      <Download className="w-4 h-4 mr-1" />
                      Download Receipt
                    </Button>
                    <Button variant="outline" size="sm" className={brandStyles.secondary} onClick={() => onBookAgain?.(booking.courseId)}>
                      Book Again
                    </Button>
                    {booking.hasReview ? (
                      <button
                        disabled
                        className="inline-flex items-center px-3 py-1.5 border-2 border-[#4b5563] bg-[#f3f4f6] text-[#4b5563] rounded-lg text-sm font-medium cursor-default"
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                        Reviewed
                      </button>
                    ) : (
                      <button
                        onClick={() => openReviewDialog(booking)}
                        className="inline-flex items-center px-3 py-1.5 border-2 border-[#2563eb] bg-transparent text-[#2563eb] rounded-lg text-sm font-medium hover:bg-[#eff6ff] transition-colors"
                      >
                        <Star className="w-3.5 h-3.5 mr-1.5" />
                        Review
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-[#F5F5DC]">
      {/* Header */}
      <Header onNavigate={onNavigate} />
      
      {/* Spacer for fixed header */}
      <div style={{ height: '64px' }}></div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h2 className={brandStyles.text}>My Bookings</h2>
          <p className="text-gray-600 mt-1">View and manage your tee time bookings</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
          <Card className="border-2 border-gray-100">
            <CardHeader className="pb-3">
              <CardDescription>Upcoming Bookings</CardDescription>
              <div className="flex items-baseline gap-2">
                <CardTitle className="text-3xl">{upcomingBookings.length}</CardTitle>
                <Calendar className={`w-5 h-5 ${brandStyles.text}`} />
              </div>
            </CardHeader>
          </Card>
          <Card className="border-2 border-gray-100">
            <CardHeader className="pb-3">
              <CardDescription>Total Rounds Played</CardDescription>
              <div className="flex items-baseline gap-2">
                <CardTitle className="text-3xl">{pastBookings.filter(b => b.status === 'completed').length}</CardTitle>
                <CheckCircle className={`w-5 h-5 ${brandStyles.text}`} />
              </div>
            </CardHeader>
          </Card>
          <Card className="border-2 border-gray-100">
            <CardHeader className="pb-3">
              <CardDescription><span className="font-bold text-[#2B8B3B]">discgo</span> Distro Fund</CardDescription>
              <div className="flex items-baseline gap-2">
                <CardTitle className="text-3xl">${[...upcomingBookings, ...pastBookings].filter(b => !b.refundedAt && b.status !== 'refunded' && new Date(b.slotStart).getFullYear() === new Date().getFullYear()).reduce((sum, b) => sum + b.distroFundContribution, 0).toFixed(2)}</CardTitle>
                <img src={basketIcon} alt="Basket" className="w-5 h-5" style={{ filter: 'brightness(0) saturate(100%) invert(37%) sepia(32%) saturate(1288%) hue-rotate(90deg) brightness(95%) contrast(85%)' }} />
              </div>
            </CardHeader>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search bookings by course name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 py-5 bg-white border-2 border-gray-200 rounded-lg"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="upcoming" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="past">Past Bookings</TabsTrigger>
          </TabsList>
          
          <TabsContent value="upcoming" className="space-y-4">
            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-4">
                {isLoading ? (
                  <Card className="border-2 border-gray-100">
                    <CardContent className="p-12 text-center">
                      <p className="text-gray-600">Loading bookings...</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {upcomingBookings
                      .filter(booking => 
                        booking.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        booking.location.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map(booking => (
                        <BookingCard key={booking.id} booking={booking} />
                      ))}
                    {upcomingBookings.length === 0 && (
                      <Card className="border-2 border-gray-100">
                        <CardContent className="p-12 text-center">
                          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600">No upcoming bookings</p>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="past" className="space-y-4">
            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-4">
                {isLoading ? (
                  <Card className="border-2 border-gray-100">
                    <CardContent className="p-12 text-center">
                      <p className="text-gray-600">Loading bookings...</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {pastBookings
                      .filter(booking => 
                        booking.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        booking.location.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map(booking => (
                        <BookingCard key={booking.id} booking={booking} isPast />
                      ))}
                    {pastBookings.length === 0 && (
                      <Card className="border-2 border-gray-100">
                        <CardContent className="p-12 text-center">
                          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600">No past bookings</p>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* discgo Distro Fund Section */}
        <div ref={distroFundRef} className="mt-12">
          <Card className="border-2 border-[#2B8B3B] bg-gradient-to-br from-white to-[#F5F5DC]">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <img src={basketIcon} alt="Basket" className="w-8 h-8" style={{ filter: 'brightness(0) saturate(100%) invert(37%) sepia(32%) saturate(1288%) hue-rotate(90deg) brightness(95%) contrast(85%)' }} />
                <CardTitle className={brandStyles.text}><span className="font-bold">discgo</span> Distro Fund</CardTitle>
              </div>
              <CardDescription>Supporting the disc golf community, one booking at a time</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700">
                  Every booking on <span className="font-bold text-[#2B8B3B]">discgo</span> automatically contributes a small portion to the community fund.
                  Each year, those funds are distributed back to participating courses so they can invest in improvements and grow.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                  <div className="flex gap-3 p-4 bg-white rounded-lg border border-gray-200">
                    <div className={`w-10 h-10 rounded-full ${brandStyles.accent} flex items-center justify-center flex-shrink-0`}>
                      <DollarSign className={`w-5 h-5 ${brandStyles.text}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 mb-1">Automated Giving</p>
                      <p className="text-sm text-gray-600">A small portion of every booking automatically goes to the community fund</p>
                    </div>
                  </div>

                  <div className="flex gap-3 p-4 bg-white rounded-lg border border-gray-200">
                    <div className={`w-10 h-10 rounded-full ${brandStyles.accent} flex items-center justify-center flex-shrink-0`}>
                      <Calendar className={`w-5 h-5 ${brandStyles.text}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 mb-1">Annual Distribution</p>
                      <p className="text-sm text-gray-600">Funds are distributed yearly to participating courses for improvements</p>
                    </div>
                  </div>

                  <div className="flex gap-3 p-4 bg-white rounded-lg border border-gray-200">
                    <div className={`w-10 h-10 rounded-full ${brandStyles.accent} flex items-center justify-center flex-shrink-0`}>
                      <MapPin className={`w-5 h-5 ${brandStyles.text}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 mb-1">Course Growth</p>
                      <p className="text-sm text-gray-600">Even low-booking courses benefit and can invest in improvements</p>
                    </div>
                  </div>

                  <div className="flex gap-3 p-4 bg-white rounded-lg border border-gray-200">
                    <div className={`w-10 h-10 rounded-full ${brandStyles.accent} flex items-center justify-center flex-shrink-0`}>
                      <Users className={`w-5 h-5 ${brandStyles.text}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 mb-1">Stronger Community</p>
                      <p className="text-sm text-gray-600">Better courses attract more players, creating a positive cycle</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-[#2B8B3B] text-white rounded-lg mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm opacity-90">Your Total Contribution</p>
                      <p className="text-3xl font-bold">${[...upcomingBookings, ...pastBookings].filter(b => !b.refundedAt && b.status !== 'refunded' && new Date(b.slotStart).getFullYear() === new Date().getFullYear()).reduce((sum, b) => sum + b.distroFundContribution, 0).toFixed(2)}</p>
                    </div>
                    <img src={basketIcon} alt="Basket" className="w-12 h-12" style={{ filter: 'brightness(0) saturate(100%) invert(100%)' }} />
                  </div>
                  <p className="text-sm opacity-90">
                    Thank you for playing on <span className="font-bold">discgo</span>. Your bookings contribute to the community fund that helps courses improve and grow every year.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Cancellation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={open => { if (!cancelLoading) setCancelDialogOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
            <DialogDescription>
              {bookingToCancel?.courseName} — {bookingToCancel?.date} at {bookingToCancel?.time}
            </DialogDescription>
          </DialogHeader>
          {bookingToCancel && (
            <div className="space-y-4 mt-2">
              {isRefundEligible(bookingToCancel) ? (
                <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">You are eligible for a full refund.</p>
                    <p className="text-green-700 mt-0.5">Your payment will be refunded once Stripe integration is active.</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">This booking is outside the refund window.</p>
                    <p className="text-red-700 mt-0.5">No refund will be issued for this cancellation.</p>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={cancelLoading}>
                  Keep Booking
                </Button>
                <Button
                  style={{ backgroundColor: '#dc2626', color: '#fff', border: 'none' }}
                  onClick={confirmCancelBooking}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? 'Cancelling…' : 'Confirm Cancellation'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#F5F5DC]">
          <DialogHeader>
            <DialogTitle className={brandStyles.text}>Review Your Experience</DialogTitle>
            <DialogDescription>
              Share your thoughts about {selectedBookingForReview?.courseName}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-6">
              {/* Course Info Card */}
              {selectedBookingForReview && (
                <Card className="border-2 border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex gap-4 items-center">
                      <ImageWithFallback
                        src={selectedBookingForReview.courseImage}
                        alt={selectedBookingForReview.courseName}
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <h4 className="mb-1">{selectedBookingForReview.courseName}</h4>
                        <p className="text-sm text-gray-600">{selectedBookingForReview.location}</p>
                        <p className="text-xs text-gray-500 mt-1">Booking #{selectedBookingForReview.confirmationNumber}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Star Rating Section */}
              <Card className="border-2 border-gray-200 bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Rate Your Experience <span className="text-red-500">*</span></CardTitle>
                  <CardDescription>How would you rate this course overall?</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    {[1, 2, 3, 4, 5].map(rating => (
                      <button
                        key={rating}
                        onClick={() => {
                          setStarRating(rating);
                          setSelectedAspects([]); // Reset aspects when rating changes
                        }}
                        onMouseEnter={() => setHoveredStar(rating)}
                        onMouseLeave={() => setHoveredStar(0)}
                        className="transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-10 h-10 transition-colors ${
                            rating <= (hoveredStar || starRating)
                              ? 'fill-[#2B8B3B] text-[#2B8B3B]'
                              : 'fill-none text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                    {starRating > 0 && (
                      <span className={`ml-2 ${brandStyles.text} font-medium`}>
                        {getRatingLabel(starRating)}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Conditional Aspects Section */}
              {starRating > 0 && (
                <>
                  {getAspectsForRating(starRating).positive.length > 0 && (
                    <Card className="border-2 border-gray-200 bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">What did you enjoy?</CardTitle>
                        <CardDescription>Select all that apply (optional)</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {getAspectsForRating(starRating).positive.map((aspect) => (
                            <button
                              key={aspect}
                              onClick={() => toggleAspect(aspect)}
                              className={`px-4 py-2 rounded-lg border-2 transition-all text-sm ${
                                selectedAspects.includes(aspect)
                                  ? 'border-[#2B8B3B] bg-[#2B8B3B]/10 text-[#2B8B3B] font-medium'
                                  : 'border-gray-200 bg-white text-gray-700 hover:border-[#2B8B3B]/50'
                              }`}
                            >
                              {aspect}
                            </button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {getAspectsForRating(starRating).negative.length > 0 && (
                    <Card className="border-2 border-gray-200 bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">What could be improved?</CardTitle>
                        <CardDescription>Select all that apply (optional)</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {getAspectsForRating(starRating).negative.map((aspect) => (
                            <button
                              key={aspect}
                              onClick={() => toggleAspect(aspect)}
                              className={`px-4 py-2 rounded-lg border-2 transition-all text-sm ${
                                selectedAspects.includes(aspect)
                                  ? 'border-orange-500 bg-orange-50 text-orange-700 font-medium'
                                  : 'border-gray-200 bg-white text-gray-700 hover:border-orange-300'
                              }`}
                            >
                              {aspect}
                            </button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Quick Questions */}
                  <Card className="border-2 border-gray-200 bg-white">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Quick Questions</CardTitle>
                      <CardDescription>Help us understand your experience better (optional)</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Would you play again? */}
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Would you play this course again?</p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setWouldPlayAgain(true)}
                            className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                              wouldPlayAgain === true
                                ? 'border-[#2B8B3B] bg-[#2B8B3B] text-white'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-[#2B8B3B]'
                            }`}
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>Yes</span>
                          </button>
                          <button
                            onClick={() => setWouldPlayAgain(false)}
                            className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                              wouldPlayAgain === false
                                ? 'border-red-500 bg-red-500 text-white'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-red-300'
                            }`}
                          >
                            <XCircle className="w-4 h-4" />
                            <span>No</span>
                          </button>
                        </div>
                      </div>

                      {/* Would you recommend? */}
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Would you recommend this course to a friend?</p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setWouldRecommend(true)}
                            className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                              wouldRecommend === true
                                ? 'border-[#2B8B3B] bg-[#2B8B3B] text-white'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-[#2B8B3B]'
                            }`}
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>Yes</span>
                          </button>
                          <button
                            onClick={() => setWouldRecommend(false)}
                            className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                              wouldRecommend === false
                                ? 'border-red-500 bg-red-500 text-white'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-red-300'
                            }`}
                          >
                            <XCircle className="w-4 h-4" />
                            <span>No</span>
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </ScrollArea>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-4">
            <Button
              variant="outline"
              onClick={() => setReviewDialogOpen(false)}
              disabled={isSubmitting}
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              className={`${brandStyles.primary} rounded-lg`}
              onClick={handleSubmitReview}
              disabled={isSubmitting || starRating === 0}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                'Submit Review'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}